import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { setSupplierCategories } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2, Tags, Lock, GitMerge, ChevronRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const slug = (v) => String(v).replace(/[^A-Za-z0-9]+/g, "-").toLowerCase();

function CategoryDialog({ edit, onClose, onSaved }) {
  const [label, setLabel] = useState("");
  const [kw, setKw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setLabel(edit?.label || ""); setKw((edit?.keywords || []).join(", ")); }, [edit]);
  const save = async () => {
    if (!label.trim()) { toast.error("Enter a category name"); return; }
    setBusy(true);
    const body = { label: label.trim(), keywords: kw.split(",").map((k) => k.trim()).filter(Boolean) };
    try {
      if (edit?.value) await api.put(`/supplier-categories/${encodeURIComponent(edit.value)}`, body);
      else await api.post("/supplier-categories", body);
      toast.success(edit?.value ? "Category updated" : "Category added");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="supplier-category-dialog">
        <DialogHeader>
          <DialogTitle>{edit?.value ? "Edit Category" : "New Supplier Category"}</DialogTitle>
          <DialogDescription>Keywords help Somcore suggest the right suppliers when an RFQ line mentions them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Category Name</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Conveyor Belting" data-testid="supplier-category-label" /></div>
          <div className="space-y-1.5"><Label>Matching Keywords <span className="font-normal text-muted-foreground">(comma separated)</span></Label><Input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="belt, conveyor, idler, pulley" data-testid="supplier-category-keywords" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="supplier-category-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{edit?.value ? "Save Changes" : "Add Category"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ src, cats, onClose, onDone }) {
  const [into, setInto] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setInto(""); }, [src]);
  const target = src ? cats.find((c) => c.value === into) : null;
  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/supplier-categories/${encodeURIComponent(src.value)}/merge`, { into });
      toast.success(`Merged into “${target.label}” — ${data.moved} supplier(s) moved`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!src} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="supplier-category-merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge “{src?.label}”</DialogTitle>
          <DialogDescription>Move every supplier into another category, combine the keywords, then remove “{src?.label}”.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Merge Into</Label>
            <Select value={into} onValueChange={setInto}>
              <SelectTrigger data-testid="supplier-category-merge-target"><SelectValue placeholder="Pick the category to keep" /></SelectTrigger>
              <SelectContent>{cats.filter((c) => c.value !== src?.value).map((c) => <SelectItem key={c.value} value={c.value} data-testid={`merge-option-${slug(c.value)}`}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {target && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="supplier-category-merge-summary">
              {src.count} supplier{src.count === 1 ? "" : "s"} will move from “{src.label}” to “{target.label}”, and “{src.label}” will be removed.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={merge} disabled={!target || busy} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="supplier-category-merge-confirm">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GitMerge className="mr-2 h-4 w-4" />}Merge</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuppliersDialog({ cat, onClose, onChanged }) {
  const [rows, setRows] = useState(null);
  const [all, setAll] = useState([]);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get(`/supplier-categories/${encodeURIComponent(cat.value)}/suppliers`).then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
  }, [cat]);
  useEffect(() => {
    if (!cat) { return; }
    setRows(null); setAdding("");
    load();
    api.get("/suppliers").then((r) => setAll(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
  }, [cat, load]);
  const tag = async (sid, add) => {
    setBusy(sid);
    try {
      await api.post(`/supplier-categories/${encodeURIComponent(cat.value)}/suppliers`, add ? { add: [sid] } : { remove: [sid] });
      toast.success(add ? "Supplier added to category" : "Supplier removed from category");
      setAdding(""); load(); onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const inIds = new Set((rows || []).map((s) => s.id));
  const addable = all.filter((s) => !inIds.has(s.id));
  return (
    <Dialog open={!!cat} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="supplier-category-suppliers-dialog">
        <DialogHeader>
          <DialogTitle>{cat?.label}</DialogTitle>
          <DialogDescription>{rows ? `${rows.length} supplier${rows.length === 1 ? "" : "s"} in this category` : "Loading…"}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Select value={adding} onValueChange={setAdding}>
            <SelectTrigger className="h-9 flex-1" data-testid="category-add-supplier-select"><SelectValue placeholder="Add a supplier to this category" /></SelectTrigger>
            <SelectContent className="max-h-72">{addable.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" className="h-9" disabled={!adding || !!busy} onClick={() => tag(adding, true)} data-testid="category-add-supplier-btn">{busy === adding && adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 sm:mr-1" />}<span className="hidden sm:inline">Add</span></Button>
        </div>        {!rows ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : !rows.length ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No suppliers use this category yet.</p>
        ) : (
          <div className="max-h-[60vh] divide-y divide-border overflow-y-auto rounded-lg border border-border">
            {rows.map((s) => (
              <div key={s.id} className="flex items-center gap-1 pr-2 transition-colors hover:bg-slate-50">
                <Link to={`/suppliers/${s.id}`} onClick={onClose} className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5" data-testid={`category-supplier-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-900">{s.name}</div>
                    {(s.country || s.type) && <div className="truncate text-xs text-muted-foreground">{[s.type && s.type.charAt(0).toUpperCase() + s.type.slice(1), s.country].filter(Boolean).join(" · ")}</div>}
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                </Link>
                <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-rose-600 hover:text-rose-700" disabled={!!busy} onClick={() => tag(s.id, false)} title="Remove from this category" data-testid={`category-remove-supplier-${s.id}`}>
                  {busy === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CategoryRow({ c, onEdit, onDelete, onMerge, onView }) {
  const inUse = c.count > 0;
  return (
    <div className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-slate-50" data-testid={`supplier-category-row-${slug(c.value)}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900">{c.label}</span>
          <button type="button" onClick={() => onView(c)} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors ${inUse ? "bg-[#E6ECF5] text-[#1B4383] hover:bg-[#D3DEEE]" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} title="See which suppliers are in this category" data-testid={`supplier-category-count-${slug(c.value)}`}>{c.count} supplier{c.count === 1 ? "" : "s"}</button>
        </div>
        <div className="mt-1 truncate text-xs text-muted-foreground" title={c.keywords?.join(", ") || ""}>{c.keywords?.length ? c.keywords.join(", ") : "No matching keywords"}</div>
      </div>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onMerge(c)} title="Merge into another category" data-testid={`supplier-category-merge-${slug(c.value)}`}><GitMerge className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(c)} data-testid={`supplier-category-edit-${slug(c.value)}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" className={`h-8 w-8 ${inUse ? "text-slate-300" : "text-rose-600 hover:text-rose-700"}`} onClick={() => onDelete(c)}
        title={inUse ? `Used by ${c.count} supplier(s) — remove it from them first` : "Remove category"} data-testid={`supplier-category-delete-${slug(c.value)}`}>
        {inUse ? <Lock className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export default function SupplierCategoriesManager() {
  const [cats, setCats] = useState(null);
  const [edit, setEdit] = useState(null);
  const [mergeSrc, setMergeSrc] = useState(null);
  const [viewCat, setViewCat] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/supplier-categories"); setCats(data.all || []); setSupplierCategories(data.all); }
    catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (c) => {
    if (c.count > 0) { toast.error(`“${c.label}” is used by ${c.count} supplier(s). Remove it from them first.`); return; }
    if (!window.confirm(`Remove the category “${c.label}”?`)) return;
    try { await api.delete(`/supplier-categories/${encodeURIComponent(c.value)}`); toast.success("Category removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <section className="mt-8 rounded-xl border border-border bg-white" data-testid="supplier-categories-section">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Tags className="h-4 w-4 text-[#A87F31]" />
          <div>
            <h2 className="text-base font-semibold text-slate-900">Supplier Categories</h2>
            <p className="text-xs text-muted-foreground">Rename, add or remove the categories you tag suppliers with.</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setEdit({})} title="New Category" aria-label="New Category" data-testid="supplier-category-add"><Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">New Category</span></Button>
      </div>
      {!cats ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
        <div className="divide-y divide-border">{cats.map((c) => <CategoryRow key={c.value} c={c} onEdit={setEdit} onDelete={remove} onMerge={setMergeSrc} onView={setViewCat} />)}</div>
      )}
      <SuppliersDialog cat={viewCat} onClose={() => setViewCat(null)} onChanged={load} />
      <MergeDialog src={mergeSrc} cats={cats || []} onClose={() => setMergeSrc(null)} onDone={() => { setMergeSrc(null); load(); }} />
      <CategoryDialog edit={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </section>
  );
}
